/**
 * V75 — Tầng truy cập dữ liệu.
 * Mọi component không gọi supabase trực tiếp cho CRUD nghiệp vụ
 * mà gọi qua module này. Giúp gom lỗi, log, retry tập trung.
 *
 * v0.4.0: Đã XOÁ toàn bộ `logActivity` khỏi client. Activity logs nay
 *         do Postgres trigger `trg_audit_<table>` (migration 005) tự
 *         động ghi server-side với JWT của caller. Lợi ích:
 *         - Không bao giờ mất audit (kể cả khi user đóng tab giữa request).
 *         - Hot-path CRUD không còn chiếm HTTP connection pool cho log.
 *         - Save batch chấm công nhanh hơn rõ rệt.
 */
import type { GuardLeave } from './guardRoster'
import { supabase, withTimeout } from '@/lib/supabase'
import type {
  ActivityLog,
  AppUser,
  AttendanceCheckin,
  AttendanceDailyRow,
  AttendanceHoliday,
  AttendanceSite,
  AttendanceSyncConfig,
  EmployeeDeptHistory,
  EmployeeSalaryHistory,
  BonusBatch,
  BonusItem,
  GuardMember,
  GuardRoster,
  GuardShiftLog,
  RemoteReason,
  AttendanceDriver,
  AttendanceOffice,
  AttendanceSecurity,
  AttendanceStatus,
  AttendanceTechnician,
  ChatChannel,
  ChatMessage,
  ChatReaction,
  Department,
  DirectoryEntry,
  DriverSource,
  EmployeeFull,
  LeaveBalance,
  LeaveRequest,
  MiaBatchTotals,
  MiaFuelItem,
  MiaInvoiceFile,
  MiaInvoiceItemTable,
  MiaOvernightItem,
  MiaPaymentBatch,
  MiaTollItem,
  NewsPost,
  NoteAck,
  OfficeDocCategory,
  OfficeDocReply,
  OfficeIncomingDoc,
  PinnedNote,
  ReactionEmoji,
  SalaryConfig,
  SalaryGrade,
  SalaryRecord,
  SalaryRecordStatus,
} from '@/lib/types'

// =============================================================
// DEPARTMENTS
// =============================================================

export async function listDepartments(): Promise<Department[]> {
  const { data, error } = await supabase
    .from('departments')
    .select('*')
    .order('code', { ascending: true })
  if (error) throw error
  return (data ?? []) as Department[]
}

export async function createDepartment(d: Omit<Department, 'id'>): Promise<Department> {
  const { data, error } = await supabase
    .from('departments')
    .insert(d)
    .select()
    .single()
  if (error) throw error
  // v0.4.0: audit đã do trigger `trg_audit_departments` ghi tự động
  return data as Department
}

export async function updateDepartment(id: string, patch: Partial<Department>): Promise<Department> {
  const { data, error } = await supabase
    .from('departments')
    .update(patch)
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  // v0.4.0: audit đã do trigger `trg_audit_departments` ghi tự động
  return data as Department
}

export async function setDepartmentActive(id: string, is_active: boolean): Promise<void> {
  const { error } = await supabase.from('departments').update({ is_active }).eq('id', id)
  if (error) throw error
  // v0.4.0: audit đã do trigger ghi tự động (UPDATE departments)
}

// =============================================================
// EMPLOYEES
// =============================================================

export async function listEmployees(filter?: {
  department_id?: string | null
  status?: string | null
  q?: string
}): Promise<EmployeeFull[]> {
  let q = supabase.from('employees').select('*').order('ho_ten', { ascending: true })
  if (filter?.department_id) q = q.eq('department_id', filter.department_id)
  if (filter?.status) q = q.eq('status', filter.status)
  if (filter?.q && filter.q.trim()) {
    const k = filter.q.trim()
    q = q.or(`ho_ten.ilike.%${k}%,cccd.ilike.%${k}%,chuc_vu.ilike.%${k}%`)
  }
  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as EmployeeFull[]
}

export async function createEmployee(e: Partial<EmployeeFull>): Promise<EmployeeFull> {
  const { data, error } = await supabase.from('employees').insert(e).select().single()
  if (error) throw error
  // v0.4.0: audit đã do trigger `trg_audit_employees` ghi tự động
  return data as EmployeeFull
}

/** Map CCCD → id của TOÀN BỘ nhân viên (mọi status) — dùng cho import Excel. */
export async function listEmployeeCccds(): Promise<Map<string, string>> {
  const { data, error } = await supabase.from('employees').select('id, cccd')
  if (error) throw error
  return new Map((data ?? []).map(r => [r.cccd as string, r.id as string]))
}

export interface BulkImportResult {
  inserted: number
  updated: number
  skipped: number
}

/**
 * Import nhân viên hàng loạt (v0.16.0). Upsert theo khoá UNIQUE `cccd`:
 * - mode 'skip'   → CCCD đã có thì bỏ qua (ON CONFLICT DO NOTHING).
 * - mode 'update' → CCCD đã có thì cập nhật đè toàn bộ cột theo file.
 * Ghi theo lô 100 dòng/lần, mỗi lô bọc withTimeout. Audit do trigger
 * `trg_audit_employees` ghi server-side như thao tác lẻ.
 */
export async function importEmployeesBulk(
  rows: Partial<EmployeeFull>[],
  mode: 'skip' | 'update',
  existingByCccd: Map<string, string>,
): Promise<BulkImportResult> {
  const CHUNK = 100
  let inserted = 0, updated = 0, skipped = 0
  for (let i = 0; i < rows.length; i += CHUNK) {
    const chunk = rows.slice(i, i + CHUNK)
    const { data, error } = await withTimeout(
      supabase
        .from('employees')
        .upsert(chunk, { onConflict: 'cccd', ignoreDuplicates: mode === 'skip' })
        .select('id, cccd'),
      30_000,
      `Import lô ${Math.floor(i / CHUNK) + 1}`,
    )
    if (error) throw error
    const written = (data ?? []) as { id: string; cccd: string }[]
    for (const w of written) {
      if (existingByCccd.has(w.cccd)) updated++
      else inserted++
    }
    skipped += chunk.length - written.length
  }
  return { inserted, updated, skipped }
}

export async function updateEmployee(id: string, patch: Partial<EmployeeFull>): Promise<EmployeeFull> {
  const { data, error } = await supabase
    .from('employees')
    .update(patch)
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  // v0.4.0: audit đã do trigger `trg_audit_employees` ghi tự động
  return data as EmployeeFull
}

/** Lịch sử công tác — RLS: admin xem hết, TP xem mốc thuộc phòng mình, NV xem của mình. */
export async function listDeptHistory(employeeId?: string): Promise<EmployeeDeptHistory[]> {
  let q = supabase.from('employee_dept_history').select('*').order('from_month')
  if (employeeId) q = q.eq('employee_id', employeeId)
  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as EmployeeDeptHistory[]
}

/** Chuyển công tác có hiệu lực từ tháng fromMonth ('YYYY-MM') — lùi về quá khứ hoặc hẹn tháng sau đều được. */
export async function transferEmployee(employeeId: string, departmentId: string, fromMonth: string, note?: string): Promise<void> {
  const { error } = await supabase.rpc('employee_transfer', {
    p_emp: employeeId, p_dept: departmentId, p_from_month: `${fromMonth.slice(0, 7)}-01`, p_note: note ?? null,
  })
  if (error) throw error
}

export async function deleteDeptHistory(id: string): Promise<void> {
  const { error } = await supabase.rpc('employee_transfer_delete', { p_id: id })
  if (error) throw error
}

// ─── Diễn biến lương (v0.47, migration 049) ──────────────────────────────────
// Thêm/sửa/xoá mốc ⇒ trigger DB tự cập nhật lương hiện tại của NV (ngạch, bậc,
// ngày hưởng bậc, TNVK, PCCV) ⇒ hạn nâng lương + cảnh báo tính theo đó.

export async function listSalaryHistory(employeeId: string): Promise<EmployeeSalaryHistory[]> {
  const { data, error } = await supabase.from('employee_salary_history').select('*')
    .eq('employee_id', employeeId)
    .order('ngay_huong', { ascending: true, nullsFirst: true })
    .order('created_at')
  if (error) throw error
  return (data ?? []) as EmployeeSalaryHistory[]
}

export type SalaryHistoryInput = Omit<EmployeeSalaryHistory, 'id' | 'created_at' | 'he_so'> & { he_so?: number | null }

export async function saveSalaryHistory(row: SalaryHistoryInput & { id?: string }): Promise<void> {
  const { id, ...rest } = row
  const payload = { ...rest, he_so: rest.he_so ?? null }
  const { error } = id
    ? await supabase.from('employee_salary_history').update(payload).eq('id', id)
    : await supabase.from('employee_salary_history').insert(payload)
  if (error) throw error
}

export async function deleteSalaryHistory(id: string): Promise<void> {
  const { error } = await supabase.from('employee_salary_history').delete().eq('id', id)
  if (error) throw error
}

// ─── Danh sách chi thưởng (v0.47, migration 050) ─────────────────────────────

export async function listBonusBatches(): Promise<BonusBatch[]> {
  const { data, error } = await supabase.from('bonus_batches').select('*').order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []) as BonusBatch[]
}

export async function saveBonusBatch(b: Partial<BonusBatch> & { ten_dot: string }): Promise<BonusBatch> {
  const { id, ...all } = b
  const rest = Object.fromEntries(Object.entries(all).filter(([k]) => k !== 'created_at' && k !== 'approved_at'))
  const q = id
    ? supabase.from('bonus_batches').update(rest).eq('id', id)
    : supabase.from('bonus_batches').insert(rest)
  const { data, error } = await q.select().single()
  if (error) throw error
  return data as BonusBatch
}

export async function deleteBonusBatch(id: string): Promise<void> {
  const { error } = await supabase.from('bonus_batches').delete().eq('id', id)
  if (error) throw error
}

export async function listBonusItems(batchId: string): Promise<BonusItem[]> {
  const { data, error } = await supabase.from('bonus_items').select('*').eq('batch_id', batchId)
  if (error) throw error
  return (data ?? []) as BonusItem[]
}

/** Ghi danh sách (thêm mới/sửa theo NV) — đợt phải đang soạn. */
export async function upsertBonusItems(rows: Omit<BonusItem, 'id'>[]): Promise<void> {
  if (rows.length === 0) return
  const { error } = await supabase.from('bonus_items').upsert(rows, { onConflict: 'batch_id,employee_id' })
  if (error) throw error
}

export async function deleteBonusItems(ids: string[]): Promise<void> {
  if (ids.length === 0) return
  const { error } = await supabase.from('bonus_items').delete().in('id', ids)
  if (error) throw error
}

export async function deleteEmployee(id: string): Promise<void> {
  // Kiểm tra dữ liệu liên quan trước khi xoá
  const checks = await Promise.all([
    supabase.from('salary_records').select('id', { count: 'exact', head: true }).eq('employee_id', id),
    supabase.from('attendance_office').select('id', { count: 'exact', head: true }).eq('employee_id', id),
    supabase.from('attendance_driver').select('id', { count: 'exact', head: true }).eq('employee_id', id),
    supabase.from('attendance_security').select('id', { count: 'exact', head: true }).eq('employee_id', id),
    supabase.from('attendance_technician').select('id', { count: 'exact', head: true }).eq('employee_id', id),
  ])
  const [salary, office, driver, security, technician] = checks
  const salaryCount = salary.count ?? 0
  const attCount = (office.count ?? 0) + (driver.count ?? 0) + (security.count ?? 0) + (technician.count ?? 0)

  if (salaryCount > 0)
    throw new Error(`Không thể xoá: nhân viên có ${salaryCount} bản ghi lương. Hãy chuyển trạng thái "Đã nghỉ việc" thay vì xoá.`)
  if (attCount > 0)
    throw new Error(`Không thể xoá: nhân viên có ${attCount} bản ghi chấm công. Hãy chuyển trạng thái "Đã nghỉ việc" thay vì xoá.`)

  // Null FK trên users trước (không xoá user account)
  const { error: userErr } = await supabase
    .from('users').update({ employee_id: null }).eq('employee_id', id)
  if (userErr) throw userErr

  const { error } = await supabase.from('employees').delete().eq('id', id)
  if (error) throw error
}

// =============================================================
// SALARY GRADES & CONFIG
// =============================================================

// Bảng hệ số bậc gần như không thay đổi trong suốt session → cache module-level.
let _gradesCache: SalaryGrade[] | null = null

export async function listSalaryGrades(): Promise<SalaryGrade[]> {
  if (_gradesCache) return _gradesCache
  const { data, error } = await supabase
    .from('salary_grades')
    .select('*')
    .order('ngach_code', { ascending: true })
    .order('bac', { ascending: true })
  if (error) throw error
  _gradesCache = (data ?? []) as SalaryGrade[]
  return _gradesCache
}

/** v0.32.0 — Thêm/sửa các bậc của một ngạch (admin_he_thong; RLS grades_admin_write).
 *  Upsert theo (ngach_code, bac). Audit do trigger ghi. */
export async function upsertSalaryGrades(rows: Omit<SalaryGrade, 'id'>[]): Promise<void> {
  const { error } = await supabase
    .from('salary_grades')
    .upsert(rows, { onConflict: 'ngach_code,bac' })
  if (error) throw error
  _gradesCache = null
}

/** v0.32.0 — Xoá các bậc theo id. FK employees(ngach_code, bac) chặn nếu đang có NV dùng. */
export async function deleteSalaryGrades(ids: string[]): Promise<void> {
  if (ids.length === 0) return
  const { error } = await supabase.from('salary_grades').delete().in('id', ids)
  _gradesCache = null
  if (error) {
    if (error.code === '23503') {
      throw new Error('Không xoá được: đang có nhân viên hoặc bảng lương gắn với ngạch/bậc này.')
    }
    throw error
  }
}

export async function listSalaryConfigs(): Promise<SalaryConfig[]> {
  const { data, error } = await supabase
    .from('salary_config')
    .select('*')
    .order('effective_date', { ascending: false })
  if (error) throw error
  return (data ?? []) as SalaryConfig[]
}

export async function createSalaryConfig(c: Omit<SalaryConfig, 'id' | 'created_at'>): Promise<SalaryConfig> {
  const { data, error } = await supabase.from('salary_config').insert(c).select().single()
  if (error) throw error
  // v0.4.0: audit đã do trigger `trg_audit_salary_config` ghi tự động
  return data as SalaryConfig
}

/** v0.17.0 — Sửa một bản cấu hình (admin_he_thong). Audit do trigger ghi. */
export async function updateSalaryConfig(id: string, patch: Partial<SalaryConfig>): Promise<SalaryConfig> {
  const { data, error } = await supabase
    .from('salary_config')
    .update(patch)
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  return data as SalaryConfig
}

/** Lấy cấu hình áp dụng cho một tháng (effective_date <= cuối tháng đó) */
export async function getActiveSalaryConfig(monthYear: string): Promise<SalaryConfig | null> {
  // monthYear: 'YYYY-MM'
  const [y, m] = monthYear.split('-').map(Number)
  // Ngày cuối tháng. KHÔNG dùng toISOString() vì nó quy về UTC → ở giờ VN
  // (UTC+7) sẽ bị lùi 1 ngày, làm sót config có effective_date đúng ngày cuối tháng.
  const lastDayNum = new Date(y, m, 0).getDate()
  const lastDay = `${monthYear}-${String(lastDayNum).padStart(2, '0')}`
  const { data, error } = await supabase
    .from('salary_config')
    .select('*')
    .lte('effective_date', lastDay)
    .order('effective_date', { ascending: false })
    .limit(1)
    .maybeSingle()
  if (error) throw error
  return (data as SalaryConfig) ?? null
}

// =============================================================
// USERS
// =============================================================

export async function listUsers(): Promise<AppUser[]> {
  const { data, error } = await supabase
    .from('users')
    .select('*')
    .order('cccd', { ascending: true })
  if (error) throw error
  return (data ?? []) as AppUser[]
}

export async function updateUserRow(id: string, patch: Partial<AppUser>): Promise<AppUser> {
  const { data, error } = await supabase
    .from('users')
    .update(patch)
    .eq('id', id)
    .select()
    .single()
  if (error) throw error
  // v0.4.0: audit đã do trigger `trg_audit_users` ghi tự động
  return data as AppUser
}

// =============================================================
// ATTENDANCE
// =============================================================

type AttTable = 'attendance_office' | 'attendance_security' | 'attendance_technician' | 'attendance_driver'

export async function listAttendanceOffice(department_id: string, month_year: string): Promise<AttendanceOffice[]> {
  const { data, error } = await supabase
    .from('attendance_office')
    .select('*')
    .eq('department_id', department_id)
    .eq('month_year', month_year)
  if (error) throw error
  return (data ?? []) as AttendanceOffice[]
}

export async function listAttendanceSecurity(department_id: string, month_year: string): Promise<AttendanceSecurity[]> {
  const { data, error } = await supabase
    .from('attendance_security')
    .select('*')
    .eq('department_id', department_id)
    .eq('month_year', month_year)
  if (error) throw error
  return (data ?? []) as AttendanceSecurity[]
}

export async function listAttendanceTechnician(department_id: string, month_year: string): Promise<AttendanceTechnician[]> {
  const { data, error } = await supabase
    .from('attendance_technician')
    .select('*')
    .eq('department_id', department_id)
    .eq('month_year', month_year)
  if (error) throw error
  return (data ?? []) as AttendanceTechnician[]
}

export async function listAttendanceDriver(
  department_id: string,
  month_year: string,
): Promise<AttendanceDriver[]> {
  const { data, error } = await supabase
    .from('attendance_driver')
    .select('*')
    .eq('department_id', department_id)
    .eq('month_year', month_year)
  if (error) throw error
  return (data ?? []) as AttendanceDriver[]
}

export async function upsertAttendance<T extends { id?: string; employee_id: string; month_year: string }>(
  table: AttTable,
  row: T,
  conflictKey: string = 'employee_id,month_year',
): Promise<void> {
  // Bỏ id rỗng để DB tự sinh khi insert
  const payload: Record<string, unknown> = { ...row }
  if (!payload.id) delete payload.id
  const { error } = await supabase.from(table).upsert(payload, { onConflict: conflictKey })
  if (error) throw error
}

/**
 * Upsert HÀNG LOẠT chấm công.
 *
 * v0.3.4: chia chunk 10 row/lần để tránh RLS evaluate quá nhiều row
 *         trong 1 statement; mỗi chunk bọc `withTimeout(12s)` cho thông báo
 *         lỗi rõ ràng nếu Supabase chậm. Log chỉ ghi 1 dòng SAU CÙNG, không
 *         kèm vào hot-path để tránh chiếm connection pool.
 */
export async function upsertAttendanceBatch<T extends { id?: string; employee_id: string; month_year: string }>(
  table: AttTable,
  rows: T[],
  conflictKey: string = 'employee_id,month_year',
): Promise<void> {
  if (rows.length === 0) return

  const payloads = rows.map((row) => {
    const p: Record<string, unknown> = { ...row }
    if (!p.id) delete p.id
    return p
  })

  const CHUNK = 10
  for (let i = 0; i < payloads.length; i += CHUNK) {
    const slice = payloads.slice(i, i + CHUNK)
    const { error } = await withTimeout(
      supabase.from(table).upsert(slice, { onConflict: conflictKey }),
      12_000,
      `upsert ${table} (chunk ${i / CHUNK + 1})`,
    )
    if (error) throw error
  }

  // v0.4.0: trigger `trg_audit_<table>` đã ghi 1 dòng/row tự động.
  // Không cần log gộp client-side nữa.
}

export async function setAttendanceStatus(
  table: AttTable,
  ids: string[],
  status: AttendanceStatus,
  meta?: { submitted_by?: string; locked_by?: string },
): Promise<void> {
  if (ids.length === 0) return
  const patch: Record<string, unknown> = { status }
  if (status === 'pending') {
    patch.submitted_at = new Date().toISOString()
    if (meta?.submitted_by) patch.submitted_by = meta.submitted_by
  }
  if (status === 'locked' && table !== 'attendance_driver') {
    patch.locked_at = new Date().toISOString()
    if (meta?.locked_by) patch.locked_by = meta.locked_by
  }
  const { error } = await supabase.from(table).update(patch).in('id', ids)
  if (error) throw error
  // v0.4.0: audit đã do trigger `trg_audit_<table>` ghi tự động (UPDATE)
}

// =============================================================
// SALARY RECORDS (Module 4)
// =============================================================

export async function listSalaryRecords(
  department_id: string | null,
  month_year: string,
): Promise<SalaryRecord[]> {
  let q = supabase
    .from('salary_records')
    .select('*')
    .eq('month_year', month_year)
    .order('ho_ten', { ascending: true })
  if (department_id) q = q.eq('department_id', department_id)
  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as SalaryRecord[]
}

/** Phiếu lương cá nhân (user) — RLS đã giới hạn employee_id = chính mình. */
export async function listMySalaryRecords(employee_id: string): Promise<SalaryRecord[]> {
  const { data, error } = await supabase
    .from('salary_records')
    .select('*')
    .eq('employee_id', employee_id)
    .order('month_year', { ascending: false })
  if (error) throw error
  return (data ?? []) as SalaryRecord[]
}

/** C3 — Tổng hợp các mục đang chờ duyệt (chấm công + lương + nghỉ phép pending). */
export interface PendingGroup {
  department_id: string
  month_year: string
  count: number
  /** Với chấm công: khối nguồn. Lương: 'salary'. Nghỉ phép: 'leave'. */
  source: 'office' | 'security' | 'technician' | 'driver' | 'salary' | 'leave'
}

export async function fetchPendingApprovals(): Promise<PendingGroup[]> {
  const att = (table: string) =>
    supabase.from(table).select('department_id, month_year').eq('status', 'pending')
  const [off, sec, tech, drv, sal, lv] = await Promise.all([
    att('attendance_office'),
    att('attendance_security'),
    att('attendance_technician'),
    att('attendance_driver'),
    supabase.from('salary_records').select('department_id, month_year').eq('status', 'pending'),
    supabase.from('leave_requests').select('department_id, from_date').eq('status', 'pending'),
  ])
  const err = off.error || sec.error || tech.error || drv.error || sal.error || lv.error
  if (err) throw err

  const groups = new Map<string, PendingGroup>()
  const add = (rows: Array<{ department_id: string; month_year: string }> | null, source: PendingGroup['source']) => {
    for (const r of rows ?? []) {
      const key = `${source}|${r.department_id}|${r.month_year}`
      const g = groups.get(key)
      if (g) g.count += 1
      else groups.set(key, { department_id: r.department_id, month_year: r.month_year, count: 1, source })
    }
  }
  add(off.data, 'office')
  add(sec.data, 'security')
  add(tech.data, 'technician')
  add(drv.data, 'driver')
  add(sal.data, 'salary')
  // Đơn nghỉ phép không có month_year — quy về tháng của from_date
  add(
    ((lv.data ?? []) as Array<{ department_id: string; from_date: string }>)
      .map(r => ({ department_id: r.department_id, month_year: r.from_date.slice(0, 7) })),
    'leave',
  )
  // Tháng mới nhất trước, trong cùng tháng thì lương trước chấm công
  return [...groups.values()].sort((a, b) =>
    b.month_year.localeCompare(a.month_year)
    || (a.source === 'salary' ? -1 : b.source === 'salary' ? 1 : 0),
  )
}

// =============================================================
// Module 6 — Trao đổi nội bộ + Ghi chú ghim (v0.20)
// =============================================================

export async function listChatChannels(): Promise<ChatChannel[]> {
  const { data, error } = await supabase
    .from('chat_channels')
    .select('*')
    .order('type', { ascending: true }) // 'department' < 'general'? không — sort client sau
  if (error) throw error
  const rows = (data ?? []) as ChatChannel[]
  // Kênh chung lên đầu, sau đó theo tên phòng
  return rows.sort((a, b) =>
    a.type === b.type ? a.name.localeCompare(b.name, 'vi') : a.type === 'general' ? -1 : 1)
}

const CHAT_PAGE_SIZE = 50

/** Trang tin nhắn mới nhất (hoặc cũ hơn mốc `before`). Trả về theo thứ tự TĂNG dần thời gian. */
export async function listChatMessages(
  channel_id: string,
  before?: string,
): Promise<{ messages: ChatMessage[]; hasMore: boolean }> {
  let q = supabase
    .from('chat_messages')
    .select('*')
    .eq('channel_id', channel_id)
    .order('created_at', { ascending: false })
    .limit(CHAT_PAGE_SIZE)
  if (before) q = q.lt('created_at', before)
  const { data, error } = await q
  if (error) throw error
  const rows = (data ?? []) as ChatMessage[]
  return { messages: rows.reverse(), hasMore: rows.length === CHAT_PAGE_SIZE }
}

export async function sendChatMessage(
  channel_id: string,
  content: string,
  reply_to?: string,
  mentions?: string[],
): Promise<ChatMessage> {
  // sender_id/sender_name do trigger chat_fill_sender điền (auth.uid() + snapshot tên)
  const { data, error } = await supabase
    .from('chat_messages')
    .insert({
      channel_id, content,
      reply_to: reply_to ?? null,
      mentions: mentions && mentions.length > 0 ? mentions : null,
    })
    .select()
    .single()
  if (error) throw error
  return data as ChatMessage
}

// ── GĐ3: nhắn riêng + @nhắc tên (v0.26) ────────────────────────────────────

/** Mở (hoặc tạo) kênh nhắn riêng với một thành viên — trả về channel id. */
export async function openDm(other_user_id: string): Promise<string> {
  const { data, error } = await supabase.rpc('chat_open_dm', { p_other: other_user_id })
  if (error) throw error
  return data as string
}

/** Danh bạ nội bộ (không gồm bản thân) cho picker DM + @mention. */
export async function fetchChatDirectory(): Promise<DirectoryEntry[]> {
  const { data, error } = await supabase.rpc('chat_directory')
  if (error) throw error
  return (data ?? []) as DirectoryEntry[]
}

/** Tin nhắn @nhắc tới mình trong N ngày gần nhất (chuông Header). */
export async function fetchMyMentions(user_id: string, days = 7): Promise<ChatMessage[]> {
  const since = new Date(Date.now() - days * 86_400_000).toISOString()
  const { data, error } = await supabase
    .from('chat_messages')
    .select('*')
    .contains('mentions', [user_id])
    .neq('sender_id', user_id)
    .is('deleted_at', null)
    .gte('created_at', since)
    .order('created_at', { ascending: false })
    .limit(20)
  if (error) throw error
  return (data ?? []) as ChatMessage[]
}

/** Gỡ tin nhắn (soft-delete): của mình ≤15 phút hoặc admin — RPC kiểm tra server-side. */
export async function deleteChatMessage(id: string): Promise<void> {
  const { error } = await supabase.rpc('chat_delete_message', { p_id: id })
  if (error) throw error
}

/** Đánh dấu đã đọc kênh tới thời điểm hiện tại. */
export async function markChannelRead(user_id: string, channel_id: string): Promise<void> {
  const { error } = await supabase
    .from('chat_reads')
    .upsert({ user_id, channel_id, last_read_at: new Date().toISOString() })
  if (error) throw error
}

/** Số tin chưa đọc theo kênh (badge Sidebar + danh sách kênh). */
export async function fetchUnreadCounts(): Promise<Map<string, number>> {
  const { data, error } = await supabase.rpc('chat_unread_counts')
  if (error) throw error
  const m = new Map<string, number>()
  for (const r of (data ?? []) as Array<{ channel_id: string; unread: number }>) {
    m.set(r.channel_id, Number(r.unread))
  }
  return m
}

// ── GĐ2: sửa tin · reaction · tìm kiếm · đính kèm · xác nhận ghim ──────────

/** Sửa tin nhắn của chính mình trong 15 phút (RPC kiểm tra server-side). */
export async function editChatMessage(id: string, content: string): Promise<void> {
  const { error } = await supabase.rpc('chat_edit_message', { p_id: id, p_content: content })
  if (error) throw error
}

/** Reactions của một trang tin nhắn đang hiển thị. */
export async function listChatReactions(messageIds: string[]): Promise<ChatReaction[]> {
  if (messageIds.length === 0) return []
  const { data, error } = await supabase
    .from('chat_reactions')
    .select('*')
    .in('message_id', messageIds)
  if (error) throw error
  return (data ?? []) as ChatReaction[]
}

export async function addReaction(message_id: string, emoji: ReactionEmoji): Promise<void> {
  // user_id/user_name/channel_id do trigger chat_reactions_fill điền
  const { error } = await supabase.from('chat_reactions').insert({ message_id, emoji })
  if (error && !error.message.includes('duplicate')) throw error
}

export async function removeReaction(message_id: string, user_id: string, emoji: ReactionEmoji): Promise<void> {
  const { error } = await supabase
    .from('chat_reactions')
    .delete()
    .eq('message_id', message_id)
    .eq('user_id', user_id)
    .eq('emoji', emoji)
  if (error) throw error
}

/** Tìm kiếm tin nhắn trong kênh (ilike, 30 kết quả mới nhất). */
export async function searchChatMessages(channel_id: string, term: string): Promise<ChatMessage[]> {
  const { data, error } = await supabase
    .from('chat_messages')
    .select('*')
    .eq('channel_id', channel_id)
    .is('deleted_at', null)
    .ilike('content', `%${term}%`)
    .order('created_at', { ascending: false })
    .limit(30)
  if (error) throw error
  return (data ?? []) as ChatMessage[]
}

const ATTACHMENT_BUCKET = 'chat-attachments'
export const ATTACHMENT_MAX_BYTES = 5 * 1024 * 1024

export interface UploadedAttachment {
  path: string
  name: string
  type: string
  size: number
}

/** Tải file đính kèm lên bucket riêng tư (đường dẫn <channel_id>/<uuid>-<tên>). */
export async function uploadChatAttachment(channel_id: string, file: File): Promise<UploadedAttachment> {
  if (file.size > ATTACHMENT_MAX_BYTES) {
    throw new Error(`File vượt quá 5MB (${(file.size / 1024 / 1024).toFixed(1)}MB).`)
  }
  const safeName = file.name.replace(/[^\p{L}\p{N}._-]+/gu, '_').slice(0, 120)
  const path = `${channel_id}/${crypto.randomUUID()}-${safeName}`
  const { error } = await supabase.storage
    .from(ATTACHMENT_BUCKET)
    .upload(path, file, { contentType: file.type || 'application/octet-stream', upsert: false })
  if (error) throw error
  return { path, name: file.name, type: file.type || 'application/octet-stream', size: file.size }
}

/** URL tải file (signed, 1 giờ) — storage RLS chỉ cho thành viên kênh. */
export async function getAttachmentUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage.from(ATTACHMENT_BUCKET).createSignedUrl(path, 3600)
  if (error) throw error
  return data.signedUrl
}

/** Gửi tin nhắn kèm file đã upload. */
export async function sendChatMessageWithAttachment(
  channel_id: string,
  content: string,
  att: UploadedAttachment,
  reply_to?: string,
  mentions?: string[],
): Promise<ChatMessage> {
  const { data, error } = await supabase
    .from('chat_messages')
    .insert({
      channel_id,
      content,
      reply_to: reply_to ?? null,
      attachment_path: att.path,
      attachment_name: att.name,
      attachment_type: att.type,
      attachment_size: att.size,
      mentions: mentions && mentions.length > 0 ? mentions : null,
    })
    .select()
    .single()
  if (error) throw error
  return data as ChatMessage
}

/** Xác nhận đã đọc ghi chú ghim (vĩnh viễn — không bỏ xác nhận). */
export async function ackNote(note_id: string): Promise<void> {
  const { error } = await supabase.from('note_acks').insert({ note_id })
  if (error && !error.message.includes('duplicate')) throw error
}

export async function listNoteAcks(noteIds: string[]): Promise<NoteAck[]> {
  if (noteIds.length === 0) return []
  const { data, error } = await supabase
    .from('note_acks')
    .select('*')
    .in('note_id', noteIds)
    .order('ack_at', { ascending: true })
  if (error) throw error
  return (data ?? []) as NoteAck[]
}

export async function listPinnedNotes(): Promise<PinnedNote[]> {
  const { data, error } = await supabase
    .from('pinned_notes')
    .select('*')
    .order('priority', { ascending: false })
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []) as PinnedNote[]
}

export type PinnedNoteInput = Pick<PinnedNote, 'scope' | 'department_id' | 'title' | 'content' | 'category'> &
  Partial<Pick<PinnedNote, 'priority' | 'effective_until' | 'require_ack'>>

export async function createPinnedNote(input: PinnedNoteInput): Promise<PinnedNote> {
  const { data, error } = await supabase.from('pinned_notes').insert(input).select().single()
  if (error) throw error
  return data as PinnedNote
}

export async function updatePinnedNote(id: string, patch: Partial<PinnedNoteInput>): Promise<PinnedNote> {
  const { data, error } = await supabase.from('pinned_notes').update(patch).eq('id', id).select().single()
  if (error) throw error
  return data as PinnedNote
}

export async function deletePinnedNote(id: string): Promise<void> {
  const { error } = await supabase.from('pinned_notes').delete().eq('id', id)
  if (error) throw error
}

// =============================================================
// Web Push (v0.27)
// =============================================================

export interface PushSubscriptionRow {
  user_id: string
  endpoint: string
  p256dh: string
  auth: string
  user_agent?: string
}

export async function savePushSubscription(sub: PushSubscriptionRow): Promise<void> {
  const { error } = await supabase
    .from('push_subscriptions')
    .upsert(sub, { onConflict: 'endpoint' })
  if (error) throw error
}

export async function deletePushSubscription(endpoint: string): Promise<void> {
  const { error } = await supabase.from('push_subscriptions').delete().eq('endpoint', endpoint)
  if (error) throw error
}

export type PushEvent = 'chat_message' | 'pin' | 'news' | 'leave'

/**
 * Báo Edge Function send-push gửi thông báo cho sự kiện vừa tạo.
 * FIRE-AND-FORGET: nuốt mọi lỗi — thông báo là phụ trợ, không chặn UX.
 */
export function notifyPush(event: PushEvent, id: string): void {
  void supabase.functions.invoke('send-push', { body: { event, id } }).catch(() => { /* phụ trợ */ })
}

// =============================================================
// Quản lý nghỉ phép (v0.25)
// =============================================================

export async function listMyLeaveRequests(employee_id: string): Promise<LeaveRequest[]> {
  const { data, error } = await supabase
    .from('leave_requests')
    .select('*')
    .eq('employee_id', employee_id)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []) as LeaveRequest[]
}

export type LeaveRequestInput = Pick<LeaveRequest, 'type' | 'from_date' | 'to_date' | 'so_ngay'> &
  Partial<Pick<LeaveRequest, 'reason'>>

/** Gửi đơn nghỉ phép — trigger server tự gán employee_id/tên/phòng của người gửi. */
export async function createLeaveRequest(input: LeaveRequestInput): Promise<LeaveRequest> {
  const { data, error } = await supabase.from('leave_requests').insert(input).select().single()
  if (error) throw error
  return data as LeaveRequest
}

/** NV huỷ đơn của mình khi còn chờ duyệt (RLS chỉ cho chuyển pending → cancelled). */
export async function cancelLeaveRequest(id: string): Promise<void> {
  const { error } = await supabase
    .from('leave_requests')
    .update({ status: 'cancelled' })
    .eq('id', id)
  if (error) throw error
}

/** Đơn chờ duyệt — RLS tự lọc: TP thấy phòng mình, admin thấy tất cả. */
export async function listPendingLeaveRequests(): Promise<LeaveRequest[]> {
  const { data, error } = await supabase
    .from('leave_requests')
    .select('*')
    .eq('status', 'pending')
    .order('created_at', { ascending: true })
  if (error) throw error
  return (data ?? []) as LeaveRequest[]
}

/** Duyệt / từ chối đơn (RPC kiểm quyền + định mức phép năm server-side). */
export async function decideLeaveRequest(id: string, approve: boolean, note?: string): Promise<void> {
  const { error } = await supabase.rpc('leave_decide', {
    p_id: id,
    p_approve: approve,
    p_note: note ?? null,
  })
  if (error) throw error
}

/** Số dư phép năm (năm nay). Bỏ trống = của chính mình. */
export async function fetchLeaveBalance(employee_id?: string): Promise<LeaveBalance> {
  const { data, error } = await supabase.rpc('leave_balance', {
    p_employee: employee_id ?? null,
  })
  if (error) throw error
  const row = (Array.isArray(data) ? data[0] : data) as LeaveBalance | undefined
  if (!row) throw new Error('Không đọc được số dư phép.')
  return { dinh_muc: Number(row.dinh_muc), da_dung: Number(row.da_dung), con_lai: Number(row.con_lai) }
}

// =============================================================
// Bản tin đơn vị (v0.23)
// =============================================================

export async function listNewsPosts(): Promise<NewsPost[]> {
  const { data, error } = await supabase
    .from('news_posts')
    .select('*')
    .order('is_pinned', { ascending: false })
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []) as NewsPost[]
}

export type NewsPostInput = Pick<NewsPost, 'title' | 'content'> &
  Partial<Pick<NewsPost, 'cover_path' | 'is_pinned' | 'category'>>

export async function createNewsPost(input: NewsPostInput): Promise<NewsPost> {
  const { data, error } = await supabase.from('news_posts').insert(input).select().single()
  if (error) throw error
  return data as NewsPost
}

export async function updateNewsPost(id: string, patch: Partial<NewsPostInput>): Promise<NewsPost> {
  const { data, error } = await supabase.from('news_posts').update(patch).eq('id', id).select().single()
  if (error) throw error
  return data as NewsPost
}

export async function deleteNewsPost(id: string): Promise<void> {
  const { error } = await supabase.from('news_posts').delete().eq('id', id)
  if (error) throw error
}

const NEWS_BUCKET = 'news-images'

/** Upload ảnh bìa bài viết (admin) — tối đa 5MB. */
export async function uploadNewsCover(file: File): Promise<string> {
  if (file.size > ATTACHMENT_MAX_BYTES) {
    throw new Error(`Ảnh vượt quá 5MB (${(file.size / 1024 / 1024).toFixed(1)}MB).`)
  }
  if (!file.type.startsWith('image/')) {
    throw new Error('Ảnh bìa phải là file hình ảnh.')
  }
  const safeName = file.name.replace(/[^\p{L}\p{N}._-]+/gu, '_').slice(0, 120)
  const path = `covers/${crypto.randomUUID()}-${safeName}`
  const { error } = await supabase.storage
    .from(NEWS_BUCKET)
    .upload(path, file, { contentType: file.type, upsert: false })
  if (error) throw error
  return path
}

/** Signed URL ảnh bìa (1 giờ) — chỉ thành viên đã đăng nhập xem được. */
export async function getNewsImageUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage.from(NEWS_BUCKET).createSignedUrl(path, 3600)
  if (error) throw error
  return data.signedUrl
}

/** Thông tin liên hệ của chính NV (A4) — đọc từ hồ sơ employees (RLS cho đọc dòng của mình). */
export async function getMyContact(
  employee_id: string,
): Promise<{ ho_ten: string; so_dien_thoai: string | null; email: string | null }> {
  const { data, error } = await supabase
    .from('employees')
    .select('ho_ten, so_dien_thoai, email')
    .eq('id', employee_id)
    .single()
  if (error) throw error
  return data as { ho_ten: string; so_dien_thoai: string | null; email: string | null }
}

/** NV tự cập nhật SĐT + email của mình qua RPC SECURITY DEFINER (migration 017). */
export async function updateMyContact(so_dien_thoai: string, email: string): Promise<void> {
  const { error } = await supabase.rpc('update_my_contact', {
    p_so_dien_thoai: so_dien_thoai,
    p_email: email,
  })
  if (error) throw error
}

/** Chấm công của chính nhân viên (A3). RLS đã cho user đọc dòng có employee_id = mình. */
export interface MyAttendance {
  office: AttendanceOffice[]
  security: AttendanceSecurity[]
  technician: AttendanceTechnician[]
  driver: AttendanceDriver[]
}

export async function listMyAttendance(employee_id: string): Promise<MyAttendance> {
  const q = (table: string) =>
    supabase.from(table).select('*').eq('employee_id', employee_id).order('month_year', { ascending: false })
  const [off, sec, tech, drv] = await Promise.all([
    q('attendance_office'),
    q('attendance_security'),
    q('attendance_technician'),
    q('attendance_driver'),
  ])
  const err = off.error || sec.error || tech.error || drv.error
  if (err) throw err
  return {
    office: (off.data ?? []) as AttendanceOffice[],
    security: (sec.data ?? []) as AttendanceSecurity[],
    technician: (tech.data ?? []) as AttendanceTechnician[],
    // Chấm công lái xe có 2 nguồn (truong_phong + admin_luong) — chỉ hiển thị bản đã khớp/chốt.
    driver: (drv.data ?? []) as AttendanceDriver[],
  }
}

export async function getSalaryRecord(
  employee_id: string,
  month_year: string,
): Promise<SalaryRecord | null> {
  const { data, error } = await supabase
    .from('salary_records')
    .select('*')
    .eq('employee_id', employee_id)
    .eq('month_year', month_year)
    .maybeSingle()
  if (error) throw error
  return (data as SalaryRecord) ?? null
}

/**
 * Upsert hàng loạt bản ghi lương (status='draft').
 * Chỉ ghi đè bản chưa approved — caller (UI) đã lọc trước khi gọi.
 */
export async function upsertSalaryRecords(
  records: Array<Partial<SalaryRecord> & { employee_id: string; month_year: string; department_id: string }>,
  created_by: string | null,
): Promise<void> {
  if (records.length === 0) return

  // PostgREST normalize toàn bộ batch thành 1 INSERT với union tất cả cột.
  // Nếu batch chứa cả bản cũ (có id) lẫn bản mới (không id), các bản mới
  // sẽ bị gán id=NULL → vi phạm NOT NULL. Fix: tách thành 2 lần gọi.
  const newRecs    = records.filter(r => r.id == null)
  const existingRecs = records.filter(r => r.id != null)

  if (newRecs.length > 0) {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const payload = newRecs.map(({ id: _id, ...r }) => ({
      ...r,
      created_by: r.created_by ?? created_by,
      status: r.status ?? 'draft',
    }))
    const { error } = await supabase.from('salary_records').insert(payload)
    if (error) throw error
  }

  if (existingRecs.length > 0) {
    const payload = existingRecs.map((r) => ({
      ...r,
      created_by: r.created_by ?? created_by,
      status: r.status ?? 'draft',
    }))
    const { error } = await supabase
      .from('salary_records')
      .upsert(payload, { onConflict: 'employee_id,month_year' })
    if (error) throw error
  }
  // v0.4.0: audit đã do trigger `trg_audit_salary_records` ghi tự động
}

export async function setSalaryRecordsStatus(
  ids: string[],
  status: SalaryRecordStatus,
  meta?: { approved_by?: string; cancelled_by?: string; cancel_reason?: string },
): Promise<void> {
  if (ids.length === 0) return
  const patch: Record<string, unknown> = { status }
  if (status === 'approved') {
    patch.approved_at = new Date().toISOString()
    if (meta?.approved_by) patch.approved_by = meta.approved_by
  }
  if (status === 'cancelled') {
    patch.cancelled_at = new Date().toISOString()
    if (meta?.cancelled_by) patch.cancelled_by = meta.cancelled_by
    if (meta?.cancel_reason) patch.cancel_reason = meta.cancel_reason
  }
  const { error } = await supabase
    .from('salary_records')
    .update(patch)
    .in('id', ids)
  if (error) throw error
  // v0.4.0: audit đã do trigger `trg_audit_salary_records` ghi tự động (UPDATE)
}

/** Xoá bảng lương theo danh sách id. Kể cả approved — trigger audit ghi nhận DELETE. */
export async function deleteSalaryRecords(ids: string[]): Promise<void> {
  if (ids.length === 0) return
  const { error } = await supabase.from('salary_records').delete().in('id', ids)
  if (error) throw error
}

/** Xoá toàn bộ bảng chấm công của một phòng+tháng (theo bảng tương ứng). */
export async function deleteAttendanceMonth(
  table: 'attendance_office' | 'attendance_security' | 'attendance_technician' | 'attendance_driver',
  department_id: string,
  month_year: string,
): Promise<number> {
  // Lấy count trước
  const { count } = await supabase
    .from(table).select('id', { count: 'exact', head: true })
    .eq('department_id', department_id).eq('month_year', month_year)
  const { error } = await supabase
    .from(table).delete()
    .eq('department_id', department_id).eq('month_year', month_year)
  if (error) throw error
  return count ?? 0
}

// =============================================================
// ACTIVITY LOGS (Module 5 — phiên 8)
// =============================================================

export interface ActivityLogFilter {
  /** CCCD người gây hành động (exact match, prefix bằng ilike) */
  user_cccd?: string | null
  /** Loại bảng (entity_type column) — insert/update/delete đều mang `TG_TABLE_NAME` */
  entity_type?: string | null
  /** Tiền tố action: `insert`, `update`, `delete` (server lưu `<op>.<table>`) */
  op?: 'insert' | 'update' | 'delete' | null
  /** Lọc theo ngày (ISO 'YYYY-MM-DD'). dateFrom inclusive, dateTo lấy đến hết 23:59:59. */
  dateFrom?: string | null
  dateTo?: string | null
}

export interface ActivityLogPage {
  rows: ActivityLog[]
  /** Tổng số dòng theo filter (HEAD count) — dùng cho pagination. */
  total: number
}

/**
 * Đọc activity_logs với filter + paginate.
 *
 * RLS `logs_admin_select` đã chặn từ DB: chỉ admin_he_thong gọi được.
 * Caller không cần check role lại, nhưng UI vẫn nên guard route trước cho UX.
 *
 * Trả về `{ rows, total }`. `total` dùng cho UI hiển thị "1–50 / 312".
 */
export async function fetchActivityLogs(
  filter: ActivityLogFilter,
  pagination: { limit: number; offset: number },
): Promise<ActivityLogPage> {
  let q = supabase
    .from('activity_logs')
    .select('*', { count: 'exact' })
    .order('created_at', { ascending: false })

  if (filter.user_cccd && filter.user_cccd.trim()) {
    q = q.ilike('user_cccd', `${filter.user_cccd.trim()}%`)
  }
  if (filter.entity_type) {
    q = q.eq('entity_type', filter.entity_type)
  }
  if (filter.op) {
    // action format: '<op>.<table>' → lọc bằng prefix
    q = q.ilike('action', `${filter.op}.%`)
  }
  if (filter.dateFrom) {
    q = q.gte('created_at', `${filter.dateFrom}T00:00:00+07:00`)
  }
  if (filter.dateTo) {
    q = q.lte('created_at', `${filter.dateTo}T23:59:59+07:00`)
  }

  q = q.range(pagination.offset, pagination.offset + pagination.limit - 1)

  const { data, error, count } = await q
  if (error) throw error
  return {
    rows: (data ?? []) as ActivityLog[],
    total: count ?? 0,
  }
}

// =============================================================
// PROMOTIONS / TNVK (Module 5 — phiên 11)
// =============================================================

/** Một dòng trả về từ RPC `check_upcoming_promotions(p_days_ahead)` (migration 008). */
export interface UpcomingPromotionRow {
  employee_id: string
  cccd: string
  ho_ten: string
  ngach_code: string
  bac: number
  ngay_huong_bac: string
  next_promotion_date: string
  days_remaining: number
}

/**
 * Lấy danh sách NV đến hạn nâng bậc trong N ngày tới (mặc định 30).
 * Gọi function PostgreSQL `public.check_upcoming_promotions(p_days_ahead)`.
 * Function đã GRANT EXECUTE cho `authenticated`, chỉ trả NV `status='dang_lam_viec'`
 * và NV CHƯA ở bậc cuối (RULE-02).
 */
export async function listUpcomingPromotions(daysAhead: number = 30): Promise<UpcomingPromotionRow[]> {
  const { data, error } = await supabase.rpc('check_upcoming_promotions', { p_days_ahead: daysAhead })
  if (error) throw error
  return (data ?? []) as UpcomingPromotionRow[]
}

/**
 * Nâng bậc 1 NV: bac += 1 + ngay_huong_bac = today.
 * Caller phải xác nhận trước. Trigger `trg_audit_employees` sẽ ghi audit
 * server-side, không cần logActivity từ client.
 *
 * Validate: bậc mới phải tồn tại trong salary_grades cùng ngach_code; nếu
 * không, trả error rõ ràng cho UI. UI cũng nên disable nút Nâng bậc khi
 * `is_bac_cuoi = TRUE`.
 */
export async function promoteEmployee(employeeId: string): Promise<EmployeeFull> {
  // 1. Lấy NV + grades song song (grades thường đã cache từ lần đầu listSalaryGrades)
  const [{ data: emp, error: empErr }, grades] = await Promise.all([
    supabase.from('employees').select('id, ngach_code, bac, ho_ten, tnvk_percent, he_so_pccv').eq('id', employeeId).maybeSingle(),
    listSalaryGrades(),
  ])
  if (empErr) throw empErr
  if (!emp) throw new Error('Không tìm thấy nhân viên.')
  if (emp.bac == null) throw new Error('NV chưa có bậc hiện tại, không thể nâng bậc.')

  const newBac = (emp.bac as number) + 1

  // 2. Verify bậc mới từ cache — không cần round-trip DB
  const grade = grades.find(g => g.ngach_code === emp.ngach_code && g.bac === newBac)
  if (!grade) {
    throw new Error(
      `Ngạch "${emp.ngach_code}" không có bậc ${newBac}. NV "${emp.ho_ten}" có thể đã ở bậc cao nhất.`,
    )
  }

  // 3. Ghi mốc diễn biến lương (v0.47) — trigger DB tự cập nhật bậc + ngày hưởng bậc của NV
  const today = new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Ho_Chi_Minh' }).format(new Date())
  const { error: hErr } = await supabase.from('employee_salary_history').insert({
    employee_id: employeeId, ngay_huong: today, loai: 'nang_bac', ngach_code: emp.ngach_code, bac: newBac,
    tnvk_percent: Number(emp.tnvk_percent ?? 0), he_so_pccv: Number(emp.he_so_pccv ?? 0),
  })
  if (hErr) throw hErr
  const { data, error } = await supabase.from('employees').select().eq('id', employeeId).single()
  if (error) throw error
  return data as EmployeeFull
}

// =============================================================
// ADMIN — Reset password (Edge Function, phiên 11)
// =============================================================

/** Response từ Edge Function `auth-lockout` (phiên 11). */
export interface AuthLockoutResponse {
  ok: true
  locked: boolean
  failed_attempts: number
  remaining_seconds: number
  remaining_attempts: number
}

/**
 * Gọi Edge Function `auth-lockout` để KIỂM TRA / TĂNG / RESET đếm số lần
 * đăng nhập sai server-side. Không bị bypass bằng F5/clear localStorage
 * như client-side cũ.
 *
 * - action='check'     : đọc trạng thái lock hiện tại (không sửa).
 * - action='increment' : tăng failed_attempts; tự lock nếu >= 5.
 * - action='reset'     : xoá failed_attempts (gọi sau login thành công).
 */
export async function callAuthLockout(
  action: 'check' | 'increment' | 'reset',
  cccd: string,
): Promise<AuthLockoutResponse> {
  const { data, error } = await supabase.functions.invoke('auth-lockout', {
    body: { action, cccd },
  })
  if (error) {
    throw new Error(error.message || `auth-lockout (${action}) thất bại.`)
  }
  if (!data?.ok) {
    throw new Error((data as { error?: string })?.error || 'auth-lockout trả response không hợp lệ.')
  }
  return data as AuthLockoutResponse
}

/**
 * Gọi Edge Function `admin-reset-password` để reset MK 1 user về `8888V75`
 * + set must_change_password=true. Edge Function dùng service_role key
 * server-side, không expose key ra client.
 *
 * Edge Function tự verify caller là admin_he_thong (đọc JWT). Nếu không
 * phải admin → trả 403.
 */
export async function adminResetPassword(targetUserId: string): Promise<{ ok: true; cccd: string } | never> {
  const { data, error } = await supabase.functions.invoke('admin-reset-password', {
    body: { user_id: targetUserId },
  })
  if (error) {
    // Edge Function trả message lỗi qua context khi status != 2xx
    throw new Error(error.message || 'Reset mật khẩu thất bại.')
  }
  if (!data?.ok) {
    throw new Error(data?.error || 'Reset mật khẩu thất bại (không rõ lý do).')
  }
  return data as { ok: true; cccd: string }
}

// ─── send-promotion-alert ─────────────────────────────────────────────────────

/**
 * Gọi Edge Function `send-promotion-alert` để gửi email cảnh báo NV sắp đến
 * hạn nâng bậc qua Resend API. Caller phải là admin_luong hoặc admin_he_thong.
 *
 * Tiền đề: secret RESEND_API_KEY + ALERT_EMAIL_TO đã set trong Supabase.
 */
export async function adminSendPromotionAlert(
  daysAhead: number = 30,
): Promise<{ ok: true; sent: boolean; count?: number; message: string } | never> {
  const { data, error } = await supabase.functions.invoke('send-promotion-alert', {
    body: { days_ahead: daysAhead },
  })
  if (error) {
    throw new Error(error.message || 'Gửi email cảnh báo thất bại.')
  }
  if (!data?.ok) {
    throw new Error(data?.error || 'Gửi email cảnh báo thất bại (không rõ lý do).')
  }
  return data as { ok: true; sent: boolean; count?: number; message: string }
}

// ─── admin-create-user ────────────────────────────────────────────────────────

export interface CreateUserPayload {
  cccd: string
  role: import('@/lib/types').UserRole
  department_id?: string
  employee_id?: string
}

/**
 * Gọi Edge Function `admin-create-user` để tạo tài khoản mới (Auth + public.users).
 * Email tự động = cccd@v75.local, MK mặc định = 8888V75, must_change_password=true.
 * Chỉ admin_he_thong mới được gọi (Edge Function verify JWT).
 */
export async function adminCreateUser(
  payload: CreateUserPayload,
): Promise<{ ok: true; cccd: string; user_id: string; message: string }> {
  const body: Record<string, string> = {
    cccd: payload.cccd,
    role: payload.role,
  }
  if (payload.department_id) body.department_id = payload.department_id
  if (payload.employee_id)   body.employee_id   = payload.employee_id

  const { data, error } = await supabase.functions.invoke('admin-create-user', { body })
  if (error) {
    throw new Error(error.message || 'Tạo tài khoản thất bại.')
  }
  if (!data?.ok) {
    throw new Error(data?.error || 'Tạo tài khoản thất bại (không rõ lý do).')
  }
  return data as { ok: true; cccd: string; user_id: string; message: string }
}

// ─── admin-delete-user ────────────────────────────────────────────────────────

/**
 * Gọi Edge Function `admin-delete-user` để xóa hoàn toàn 1 tài khoản:
 * - Xóa khỏi public.users
 * - Xóa khỏi Supabase Auth
 * - Ghi audit log action='admin.delete_user'
 *
 * Bảo vệ server-side: không xóa chính mình, không xóa admin_he_thong duy nhất.
 * Chỉ admin_he_thong được gọi (Edge Function verify JWT).
 */
export async function adminDeleteUser(
  targetUserId: string,
): Promise<{ ok: true; cccd: string; message: string }> {
  const { data, error } = await supabase.functions.invoke('admin-delete-user', {
    body: { user_id: targetUserId },
  })
  if (error) {
    throw new Error(error.message || 'Xóa tài khoản thất bại.')
  }
  if (!data?.ok) {
    throw new Error(data?.error || 'Xóa tài khoản thất bại (không rõ lý do).')
  }
  return data as { ok: true; cccd: string; message: string }
}

// =============================================================
// DASHBOARD KPI (phiên 19B)
// =============================================================

export interface DashboardKpi {
  soNVDangLam: number
  tongThucLinhThang: number
  soBangLuongChoDuyet: number
  soNVSapNangBac: number
  currentMonth: string
}

export async function fetchDashboardKpi(): Promise<DashboardKpi> {
  const now = new Date()
  const currentMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`

  const [empRes, salaryRes, pendingRes, promotionRes] = await Promise.all([
    supabase.from('employees').select('id', { count: 'exact', head: true }).eq('status', 'dang_lam_viec'),
    supabase.from('salary_records').select('thuc_linh').eq('month_year', currentMonth).eq('status', 'approved'),
    supabase.from('salary_records').select('id', { count: 'exact', head: true }).eq('status', 'pending'),
    supabase.rpc('check_upcoming_promotions', { p_days_ahead: 30 }),
  ])

  const tongThucLinh = ((salaryRes.data ?? []) as Array<{ thuc_linh: unknown }>)
    .reduce((s, r) => s + (Number(r.thuc_linh ?? 0) || 0), 0)

  return {
    soNVDangLam:         empRes.count ?? 0,
    tongThucLinhThang:   tongThucLinh,
    soBangLuongChoDuyet: pendingRes.count ?? 0,
    soNVSapNangBac:      ((promotionRes.data ?? []) as unknown[]).length,
    currentMonth,
  }
}

// =============================================================
// REPORTS (Module 6 — phiên 15)
// =============================================================

/**
 * Lấy tất cả salary_records cho 1 kỳ lương (không lọc theo phòng).
 * Dùng cho trang Báo cáo tổng hợp (/reports).
 * RLS cho phép admin_luong + admin_he_thong SELECT mọi phòng.
 */
export async function fetchSalaryReport(
  month_year: string,
  status?: SalaryRecordStatus | null,
): Promise<SalaryRecord[]> {
  let q = supabase
    .from('salary_records')
    .select('*')
    .eq('month_year', month_year)
    .order('department_id', { ascending: true })
    .order('ho_ten', { ascending: true })
  if (status) q = q.eq('status', status)
  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as SalaryRecord[]
}

// ─── Salary trend (phiên 16) ──────────────────────────────────────────────────

export interface SalaryTrendPoint {
  month_year: string
  tongQuiLuong: number
  tongThucLinh: number
  tongThueTNCN: number
  tongTrichNLD: number
  soNV: number
}

/**
 * Lấy dữ liệu xu hướng quỹ lương cho `months` tháng gần nhất.
 * Dùng RPC get_salary_trend (migration 011) — aggregate server-side (GROUP BY month_year).
 * Trả về 1 dòng/tháng thay vì toàn bộ salary_records → giảm data transfer đáng kể.
 */
export async function fetchSalaryTrend(months: number = 12): Promise<SalaryTrendPoint[]> {
  const { data, error } = await supabase.rpc('get_salary_trend', { p_months: months })
  if (error) throw error
  const n = (v: unknown) => { const x = Number(v ?? 0); return isFinite(x) ? x : 0 }
  return ((data ?? []) as Array<Record<string, unknown>>).map(r => ({
    month_year:   String(r.month_year),
    tongQuiLuong: n(r.tong_qui_luong),
    tongThucLinh: n(r.tong_thuc_linh),
    tongThueTNCN: n(r.tong_thue_tncn),
    tongTrichNLD: n(r.tong_trich_nld),
    soNV:         n(r.so_nv),
  }))
}

// =============================================================
// DRIVER MATCH — RULE-06 (khôi phục sau truncation phiên 16)
// =============================================================

/**
 * Cập nhật cờ is_matched cho cặp 2 nguồn lái xe theo công thức RULE-06.
 * So sánh 11 chỉ tiêu giữa nguồn truong_phong và admin_luong.
 * Dùng tolerance Math.abs(a-b) < 0.001 thay vì strict equality.
 */
export async function refreshDriverMatch(
  department_id: string,
  month_year: string,
): Promise<{ matched: number; mismatched: number }> {
  const FIELDS: Array<keyof AttendanceDriver> = [
    's600', 'xe_4_7', 'xe_16_29', 'cong_mia', 'nhan_cong',
    'cong_cho', 'so_km', 'cong_t7cn', 'ngoai_gio', 'le_tet_di_lam', 'le_tet_hoc_phep',
  ]
  const TOL = 0.001

  const { data, error } = await supabase
    .from('attendance_driver')
    .select('*')
    .eq('department_id', department_id)
    .eq('month_year', month_year)
  if (error) throw error

  const rows = (data ?? []) as AttendanceDriver[]

  // Group by employee_id
  const byEmp = new Map<string, { tp?: AttendanceDriver; al?: AttendanceDriver }>()
  for (const r of rows) {
    const slot = byEmp.get(r.employee_id) ?? {}
    if ((r.source as DriverSource) === 'truong_phong') slot.tp = r
    else slot.al = r
    byEmp.set(r.employee_id, slot)
  }

  let matched = 0
  let mismatched = 0
  const updates: Array<{ id: string; is_matched: boolean }> = []

  byEmp.forEach(({ tp, al }) => {
    if (!tp || !al) return
    const ok = FIELDS.every(f => {
      const a = Number(tp[f] ?? 0)
      const b = Number(al[f] ?? 0)
      return Math.abs(a - b) < TOL
    })
    if (ok) matched++
    else mismatched++
    updates.push({ id: tp.id, is_matched: ok })
    updates.push({ id: al.id, is_matched: ok })
  })

  // Batch upsert is_matched (1 request thay vì N)
  if (updates.length > 0) {
    const { error: upsertError } = await supabase
      .from('attendance_driver')
      .upsert(updates, { onConflict: 'id' })
    if (upsertError) throw upsertError
  }

  return { matched, mismatched }
}

// =============================================================
// Thanh toán Mia (v0.29, migration 026)
// =============================================================

export async function listMyMiaBatches(employee_id: string): Promise<MiaPaymentBatch[]> {
  const { data, error } = await supabase
    .from('mia_payment_batches')
    .select('*')
    .eq('employee_id', employee_id)
    .order('created_at', { ascending: false })
  if (error) throw error
  return (data ?? []) as MiaPaymentBatch[]
}

/** Đợt chờ duyệt — RLS tự lọc: TP thấy đợt của đội mình, admin thấy tất cả. */
export async function listPendingMiaBatches(): Promise<MiaPaymentBatch[]> {
  const { data, error } = await supabase
    .from('mia_payment_batches')
    .select('*')
    .eq('status', 'pending')
    .order('submitted_at', { ascending: true })
  if (error) throw error
  return (data ?? []) as MiaPaymentBatch[]
}

/** Đợt đã duyệt, chờ Kế toán đánh dấu đã thanh toán. */
export async function listApprovedMiaBatches(): Promise<MiaPaymentBatch[]> {
  const { data, error } = await supabase
    .from('mia_payment_batches')
    .select('*')
    .eq('status', 'approved')
    .order('decided_at', { ascending: true })
  if (error) throw error
  return (data ?? []) as MiaPaymentBatch[]
}

export type MiaBatchInput = Pick<MiaPaymentBatch, 'mia_dot' | 'tu_ngay' | 'den_ngay'> &
  Partial<Pick<MiaPaymentBatch,
    'doi' | 'mac_xe' | 'bien_so' | 'gddd_so' | 'ly_do' |
    'tam_ung_da_nhan' | 'hinh_thuc_tt' | 'so_tai_khoan' | 'ten_ngan_hang'>>

/** Tạo đợt mới (nháp) — trigger server tự gán employee_id/tên/đội/phòng + STK mặc định theo hồ sơ NV. */
export async function createMiaBatch(input: MiaBatchInput): Promise<MiaPaymentBatch> {
  const { data, error } = await supabase.from('mia_payment_batches').insert(input).select().single()
  if (error) throw error
  return data as MiaPaymentBatch
}

/** Sửa đợt (chỉ khi còn draft/rejected — RLS canh trạng thái). */
export async function updateMiaBatch(id: string, patch: Partial<MiaBatchInput>): Promise<MiaPaymentBatch> {
  const { data, error } = await supabase.from('mia_payment_batches').update(patch).eq('id', id).select().single()
  if (error) throw error
  return data as MiaPaymentBatch
}

/** Nộp đợt (draft/rejected → pending) — RLS chỉ cho phép, trigger validate có ít nhất 1 dòng kê. */
export async function submitMiaBatch(id: string): Promise<void> {
  const { error } = await supabase.from('mia_payment_batches').update({ status: 'pending' }).eq('id', id)
  if (error) throw error
}

export async function deleteMiaBatch(id: string): Promise<void> {
  const { error } = await supabase.from('mia_payment_batches').delete().eq('id', id)
  if (error) throw error
}

/** Duyệt / từ chối (RPC kiểm quyền server-side: TP đúng đội hoặc admin_luong). */
export async function decideMiaBatch(id: string, approve: boolean, note?: string): Promise<void> {
  const { error } = await supabase.rpc('mia_batch_decide', { p_id: id, p_approve: approve, p_note: note ?? null })
  if (error) throw error
}

/** Đánh dấu đã thanh toán (RPC — chỉ Kế toán / admin_luong, đợt phải đã duyệt). */
export async function markMiaBatchPaid(id: string, note?: string): Promise<void> {
  const { error } = await supabase.rpc('mia_batch_mark_paid', { p_id: id, p_note: note ?? null })
  if (error) throw error
}

/** Tổng hợp số tiền cho tab Giấy đề nghị thanh toán (server tính, không cho gõ tay). */
export async function fetchMiaBatchTotals(id: string): Promise<MiaBatchTotals> {
  const { data, error } = await supabase.rpc('mia_batch_totals', { p_id: id })
  if (error) throw error
  const row = (Array.isArray(data) ? data[0] : data) as Record<string, unknown> | undefined
  if (!row) throw new Error('Không tính được tổng số tiền đợt thanh toán.')
  return {
    tien_nhien_lieu: Number(row.tien_nhien_lieu ?? 0),
    tien_ve_cau: Number(row.tien_ve_cau ?? 0),
    tien_luu_dem: Number(row.tien_luu_dem ?? 0),
    tam_ung_da_nhan: Number(row.tam_ung_da_nhan ?? 0),
    so_tien_duoc_linh: Number(row.so_tien_duoc_linh ?? 0),
    so_hoa_don_kem: Number(row.so_hoa_don_kem ?? 0),
  }
}

// ── Bảng kê xăng dầu ──

export async function listMiaFuelItems(batch_id: string): Promise<MiaFuelItem[]> {
  const { data, error } = await supabase.from('mia_fuel_items').select('*').eq('batch_id', batch_id).order('ngay', { ascending: true })
  if (error) throw error
  return (data ?? []) as MiaFuelItem[]
}

export type MiaFuelItemInput = Pick<MiaFuelItem, 'batch_id'> &
  Partial<Pick<MiaFuelItem, 'so_hoa_don' | 'ngay' | 'don_vi_ban' | 'so_luong_lit' | 'thue_gtgt' | 'tong_cong' | 'ghi_chu'>>

export async function createMiaFuelItem(input: MiaFuelItemInput): Promise<MiaFuelItem> {
  const { data, error } = await supabase.from('mia_fuel_items').insert(input).select().single()
  if (error) throw error
  return data as MiaFuelItem
}

export async function updateMiaFuelItem(id: string, patch: Partial<MiaFuelItemInput>): Promise<MiaFuelItem> {
  const { data, error } = await supabase.from('mia_fuel_items').update(patch).eq('id', id).select().single()
  if (error) throw error
  return data as MiaFuelItem
}

export async function deleteMiaFuelItem(id: string): Promise<void> {
  const { error } = await supabase.from('mia_fuel_items').delete().eq('id', id)
  if (error) throw error
}

// ── Bảng kê phí cầu đường ──

export async function listMiaTollItems(batch_id: string): Promise<MiaTollItem[]> {
  const { data, error } = await supabase.from('mia_toll_items').select('*').eq('batch_id', batch_id).order('created_at', { ascending: true })
  if (error) throw error
  return (data ?? []) as MiaTollItem[]
}

export type MiaTollItemInput = Pick<MiaTollItem, 'batch_id'> &
  Partial<Pick<MiaTollItem, 'so_lenh' | 'thang_thuc_hien' | 'tuyen_duong' | 've_etc_sl' | 've_etc_tien' | 've_giay_sl' | 've_giay_tien' | 'ghi_chu'>>

export async function createMiaTollItem(input: MiaTollItemInput): Promise<MiaTollItem> {
  const { data, error } = await supabase.from('mia_toll_items').insert(input).select().single()
  if (error) throw error
  return data as MiaTollItem
}

export async function updateMiaTollItem(id: string, patch: Partial<MiaTollItemInput>): Promise<MiaTollItem> {
  const { data, error } = await supabase.from('mia_toll_items').update(patch).eq('id', id).select().single()
  if (error) throw error
  return data as MiaTollItem
}

export async function deleteMiaTollItem(id: string): Promise<void> {
  const { error } = await supabase.from('mia_toll_items').delete().eq('id', id)
  if (error) throw error
}

// ── Bảng kê thanh toán lưu đêm ──

export async function listMiaOvernightItems(batch_id: string): Promise<MiaOvernightItem[]> {
  const { data, error } = await supabase.from('mia_overnight_items').select('*').eq('batch_id', batch_id).order('tu_ngay', { ascending: true })
  if (error) throw error
  return (data ?? []) as MiaOvernightItem[]
}

export type MiaOvernightItemInput = Pick<MiaOvernightItem, 'batch_id'> &
  Partial<Pick<MiaOvernightItem, 'noi_cong_tac' | 'tu_ngay' | 'den_ngay' | 'so_dem' | 'phu_cap_dem' | 'ghi_chu'>>

export async function createMiaOvernightItem(input: MiaOvernightItemInput): Promise<MiaOvernightItem> {
  const { data, error } = await supabase.from('mia_overnight_items').insert(input).select().single()
  if (error) throw error
  return data as MiaOvernightItem
}

export async function updateMiaOvernightItem(id: string, patch: Partial<MiaOvernightItemInput>): Promise<MiaOvernightItem> {
  const { data, error } = await supabase.from('mia_overnight_items').update(patch).eq('id', id).select().single()
  if (error) throw error
  return data as MiaOvernightItem
}

export async function deleteMiaOvernightItem(id: string): Promise<void> {
  const { error } = await supabase.from('mia_overnight_items').delete().eq('id', id)
  if (error) throw error
}

// ── Hoá đơn / chứng từ đính kèm ──

const MIA_INVOICE_BUCKET = 'mia-invoices'
export const MIA_INVOICE_MAX_BYTES = 15 * 1024 * 1024

export interface MiaUploadedInvoice {
  path: string
  name: string
  type: string
  size: number
}

/** Tải file hoá đơn (PDF/XML) lên bucket riêng tư (đường dẫn <batch_id>/<uuid>-<tên gốc>). */
export async function uploadMiaInvoiceFile(batch_id: string, file: File): Promise<MiaUploadedInvoice> {
  if (file.size > MIA_INVOICE_MAX_BYTES) {
    throw new Error(`File vượt quá 15MB (${(file.size / 1024 / 1024).toFixed(1)}MB).`)
  }
  const safeName = file.name.replace(/[^\p{L}\p{N}._-]+/gu, '_').slice(0, 120)
  const path = `${batch_id}/${crypto.randomUUID()}-${safeName}`
  const { error } = await supabase.storage
    .from(MIA_INVOICE_BUCKET)
    .upload(path, file, { contentType: file.type || 'application/octet-stream', upsert: false })
  if (error) throw error
  return { path, name: file.name, type: file.type || 'application/octet-stream', size: file.size }
}

/** Ghi nhận file đã upload vào 1 dòng kê (hoặc cả đợt nếu item_id/item_table = 'batch'). */
export async function saveMiaInvoiceFile(
  batch_id: string,
  item_table: MiaInvoiceItemTable,
  item_id: string | null,
  att: MiaUploadedInvoice,
  extracted?: Record<string, unknown> | null,
): Promise<MiaInvoiceFile> {
  const { data, error } = await supabase
    .from('mia_invoice_files')
    .insert({
      batch_id, item_table, item_id,
      storage_path: att.path, file_name: att.name, file_type: att.type, file_size: att.size,
      extracted: extracted ?? null,
    })
    .select()
    .single()
  if (error) throw error
  return data as MiaInvoiceFile
}

export async function listMiaInvoiceFiles(batch_id: string): Promise<MiaInvoiceFile[]> {
  const { data, error } = await supabase.from('mia_invoice_files').select('*').eq('batch_id', batch_id).order('created_at', { ascending: true })
  if (error) throw error
  return (data ?? []) as MiaInvoiceFile[]
}

/** URL tải file hoá đơn (signed, 1 giờ) — storage RLS chỉ cho người có quyền xem đợt. */
export async function getMiaInvoiceUrl(path: string): Promise<string> {
  const { data, error } = await supabase.storage.from(MIA_INVOICE_BUCKET).createSignedUrl(path, 3600)
  if (error) throw error
  return data.signedUrl
}

/** Xoá file đính kèm (storage + bản ghi) — chỉ khi đợt còn editable (RLS canh). */
export async function deleteMiaInvoiceFile(id: string, storage_path: string): Promise<void> {
  const { error: delErr } = await supabase.from('mia_invoice_files').delete().eq('id', id)
  if (delErr) throw delErr
  await supabase.storage.from(MIA_INVOICE_BUCKET).remove([storage_path]).catch(() => { /* không chặn nếu storage đã dọn trước */ })
}

// =============================================================
// VĂN PHÒNG CÁ NHÂN — VĂN BẢN ĐẾN (v0.33, migration 027)
// RLS: chỉ chủ sở hữu (owner_id = auth.uid()) — kể cả admin không xem được.
// =============================================================

const OFFICE_DOCS_BUCKET = 'office-docs'
export const OFFICE_DOC_MAX_BYTES = 20 * 1024 * 1024

export type OfficeCategoryInput = Pick<OfficeDocCategory, 'name' | 'keywords' | 'color' | 'sort_order'>
export type OfficeDocInput = Partial<Pick<OfficeIncomingDoc,
  'category_id' | 'file_name' | 'noi_ban_hanh' | 'so_ky_hieu' | 'ngay_van_ban' | 'trich_yeu' |
  'deadline' | 'deadline_note' | 'note' | 'is_done' | 'content_text'>>

export async function listOfficeCategories(): Promise<OfficeDocCategory[]> {
  const { data, error } = await supabase
    .from('office_doc_categories')
    .select('*')
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: true })
  if (error) throw error
  return (data ?? []) as OfficeDocCategory[]
}

export async function createOfficeCategories(rows: OfficeCategoryInput[]): Promise<OfficeDocCategory[]> {
  const { data, error } = await supabase.from('office_doc_categories').insert(rows).select()
  if (error) throw error
  return (data ?? []) as OfficeDocCategory[]
}

export async function updateOfficeCategory(id: string, patch: Partial<OfficeCategoryInput>): Promise<void> {
  const { error } = await supabase.from('office_doc_categories').update(patch).eq('id', id)
  if (error) throw error
}

/** Xoá đầu mục — văn bản thuộc đầu mục chuyển về "Chưa phân loại" (FK on delete set null). */
export async function deleteOfficeCategory(id: string): Promise<void> {
  const { error } = await supabase.from('office_doc_categories').delete().eq('id', id)
  if (error) throw error
}

/** Danh sách không kéo content_text (tới 60k ký tự/văn bản) — chỉ nạp khi phân loại lại. */
const OFFICE_DOC_LIST_COLUMNS = 'id, owner_id, category_id, file_name, original_name, storage_path, file_type, file_size, uploaded_at, noi_ban_hanh, so_ky_hieu, ngay_van_ban, trich_yeu, deadline, deadline_note, note, is_done, done_at, updated_at'

export async function listOfficeIncomingDocs(): Promise<OfficeIncomingDoc[]> {
  const { data, error } = await withTimeout(
    supabase
      .from('office_incoming_docs')
      .select(OFFICE_DOC_LIST_COLUMNS)
      .order('uploaded_at', { ascending: false }),
  )
  if (error) throw error
  return (data ?? []) as OfficeIncomingDoc[]
}

/** Tải file lên bucket riêng (<uid>/<uuid>.<ext>) rồi ghi bản ghi văn bản đến. */
export async function createOfficeIncomingDoc(
  owner_id: string,
  file: File,
  fields: OfficeDocInput & { file_name: string },
): Promise<OfficeIncomingDoc> {
  if (file.size > OFFICE_DOC_MAX_BYTES) {
    throw new Error(`File "${file.name}" vượt quá 20MB (${(file.size / 1024 / 1024).toFixed(1)}MB).`)
  }
  const dot = file.name.lastIndexOf('.')
  const ext = dot > 0 ? file.name.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 8) : ''
  const path = `${owner_id}/${crypto.randomUUID()}${ext ? '.' + ext : ''}`
  const { error: upErr } = await supabase.storage
    .from(OFFICE_DOCS_BUCKET)
    .upload(path, file, { contentType: file.type || 'application/octet-stream', upsert: false })
  if (upErr) throw upErr
  const { data, error } = await supabase
    .from('office_incoming_docs')
    .insert({
      ...fields,
      original_name: file.name,
      storage_path: path,
      file_type: file.type || null,
      file_size: file.size,
    })
    .select(OFFICE_DOC_LIST_COLUMNS)
    .single()
  if (error) {
    await supabase.storage.from(OFFICE_DOCS_BUCKET).remove([path]).catch(() => { /* dọn file mồ côi */ })
    throw error
  }
  return data as OfficeIncomingDoc
}

/** Nội dung text đã trích của các văn bản (để phân loại lại theo từ khoá mới). */
export async function listOfficeDocTexts(ids: string[]): Promise<Map<string, string>> {
  const out = new Map<string, string>()
  if (ids.length === 0) return out
  const { data, error } = await supabase.from('office_incoming_docs').select('id, content_text').in('id', ids)
  if (error) throw error
  for (const r of (data ?? []) as { id: string; content_text: string | null }[]) out.set(r.id, r.content_text ?? '')
  return out
}

export async function updateOfficeIncomingDoc(id: string, patch: OfficeDocInput): Promise<OfficeIncomingDoc> {
  const { data, error } = await supabase.from('office_incoming_docs').update(patch).eq('id', id).select(OFFICE_DOC_LIST_COLUMNS).single()
  if (error) throw error
  return data as OfficeIncomingDoc
}

/** Xoá nhiều văn bản (bản ghi + file gốc + file văn bản trả lời). */
export async function deleteOfficeIncomingDocs(docs: Pick<OfficeIncomingDoc, 'id' | 'storage_path'>[]): Promise<void> {
  if (docs.length === 0) return
  const ids = docs.map(d => d.id)
  // File văn bản trả lời: bản ghi tự xoá theo FK cascade, file storage phải dọn tay
  const { data: replies } = await supabase.from('office_doc_replies').select('storage_path').in('doc_id', ids)
  const { error } = await supabase.from('office_incoming_docs').delete().in('id', ids)
  if (error) throw error
  const paths = [...docs.map(d => d.storage_path), ...((replies ?? []) as { storage_path: string }[]).map(r => r.storage_path)]
  await supabase.storage.from(OFFICE_DOCS_BUCKET).remove(paths)
    .catch(() => { /* không chặn nếu storage đã dọn trước */ })
}

/** URL xem/tải (signed 1 giờ). `downloadAs` → trình duyệt tải về với tên file theo văn bản. */
export async function getOfficeDocUrl(path: string, downloadAs?: string): Promise<string> {
  const { data, error } = await supabase.storage
    .from(OFFICE_DOCS_BUCKET)
    .createSignedUrl(path, 3600, downloadAs ? { download: downloadAs } : undefined)
  if (error) throw error
  return data.signedUrl
}

// ── Văn bản trả lời / kết quả xử lý (migration 028) ──

export async function listOfficeDocReplies(): Promise<OfficeDocReply[]> {
  const { data, error } = await supabase
    .from('office_doc_replies')
    .select('*')
    .order('uploaded_at', { ascending: true })
  if (error) throw error
  return (data ?? []) as OfficeDocReply[]
}

/** Tải văn bản trả lời lên (<uid>/replies/<uuid>.<ext>) và gắn vào văn bản đến. */
export async function uploadOfficeDocReply(owner_id: string, doc_id: string, file: File): Promise<OfficeDocReply> {
  if (file.size > OFFICE_DOC_MAX_BYTES) {
    throw new Error(`File "${file.name}" vượt quá 20MB (${(file.size / 1024 / 1024).toFixed(1)}MB).`)
  }
  const dot = file.name.lastIndexOf('.')
  const ext = dot > 0 ? file.name.slice(dot + 1).toLowerCase().replace(/[^a-z0-9]/g, '').slice(0, 8) : ''
  const path = `${owner_id}/replies/${crypto.randomUUID()}${ext ? '.' + ext : ''}`
  const { error: upErr } = await supabase.storage
    .from(OFFICE_DOCS_BUCKET)
    .upload(path, file, { contentType: file.type || 'application/octet-stream', upsert: false })
  if (upErr) throw upErr
  const { data, error } = await supabase
    .from('office_doc_replies')
    .insert({ doc_id, file_name: file.name, storage_path: path, file_type: file.type || null, file_size: file.size })
    .select()
    .single()
  if (error) {
    await supabase.storage.from(OFFICE_DOCS_BUCKET).remove([path]).catch(() => { /* dọn file mồ côi */ })
    throw error
  }
  return data as OfficeDocReply
}

export async function deleteOfficeDocReply(reply: Pick<OfficeDocReply, 'id' | 'storage_path'>): Promise<void> {
  const { error } = await supabase.from('office_doc_replies').delete().eq('id', reply.id)
  if (error) throw error
  await supabase.storage.from(OFFICE_DOCS_BUCKET).remove([reply.storage_path]).catch(() => { /* đã dọn trước */ })
}

// =============================================================
// CHẤM CÔNG THEO NGÀY + ĐIỂM DANH GPS (v0.35, migration 030)
// =============================================================

export async function listAttendanceSites(): Promise<AttendanceSite[]> {
  const { data, error } = await supabase.from('attendance_sites').select('*').order('created_at')
  if (error) throw error
  return (data ?? []) as AttendanceSite[]
}

export async function saveAttendanceSite(
  s: Partial<AttendanceSite> & Pick<AttendanceSite, 'name' | 'lat' | 'lng' | 'radius_m'>,
): Promise<void> {
  const payload = {
    name: s.name, lat: s.lat, lng: s.lng, radius_m: s.radius_m, is_active: s.is_active ?? true,
    allowed_ips: s.allowed_ips ?? [],
  }
  const { error } = s.id
    ? await supabase.from('attendance_sites').update(payload).eq('id', s.id)
    : await supabase.from('attendance_sites').insert(payload)
  if (error) throw error
}

export async function deleteAttendanceSite(id: string): Promise<void> {
  const { error } = await supabase.from('attendance_sites').delete().eq('id', id)
  if (error) throw error
}

/** Điểm danh ngoài — lưu GPS + lý do, chờ trưởng phòng/admin lương duyệt mới tính công. */
export async function checkInRemote(
  kind: 'in' | 'out',
  pos: { lat: number; lng: number; accuracy: number } | null,
  device: { id: string; kind: 'mobile' | 'desktop' },
  reason: RemoteReason,
  note: string,
): Promise<AttendanceCheckin> {
  const { data, error } = await supabase.rpc('attendance_check_in_remote', {
    p_kind: kind, p_lat: pos?.lat ?? null, p_lng: pos?.lng ?? null, p_accuracy: pos?.accuracy ?? null,
    p_device_id: device.id, p_device_kind: device.kind, p_reason: reason, p_note: note,
  })
  if (error) throw error
  return data as AttendanceCheckin
}

export type RemoteCheckinRow = AttendanceCheckin & {
  employees: { ho_ten: string; department_id: string | null } | null
}

/** Lượt điểm danh ngoài đang chờ duyệt — RLS tự giới hạn: admin lương xem hết, TP xem phòng mình. */
export async function listPendingRemoteCheckins(employeeIds?: string[]): Promise<RemoteCheckinRow[]> {
  let q = supabase.from('attendance_checkins')
    .select('*, employees(ho_ten, department_id)')
    .eq('review_status', 'pending')
    .order('checked_at')
  if (employeeIds) q = q.in('employee_id', employeeIds.length ? employeeIds : ['00000000-0000-0000-0000-000000000000'])
  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as RemoteCheckinRow[]
}

export async function decideRemoteCheckin(id: string, approve: boolean): Promise<void> {
  const { error } = await supabase.rpc('attendance_remote_decide', { p_id: id, p_approve: approve })
  if (error) throw error
}

/** IP công cộng mà máy chủ nhìn thấy cho thiết bị đang dùng — để khai báo IP mạng cơ quan. */
export async function getMyPublicIp(): Promise<string | null> {
  const { data, error } = await supabase.rpc('attendance_my_ip')
  if (error) throw error
  return (data as string | null) ?? null
}

export async function listHolidays(from?: string, to?: string): Promise<AttendanceHoliday[]> {
  let q = supabase.from('attendance_holidays').select('*').order('ngay')
  if (from) q = q.gte('ngay', from)
  if (to) q = q.lte('ngay', to)
  const { data, error } = await q
  if (error) throw error
  return (data ?? []) as AttendanceHoliday[]
}

export async function saveHoliday(h: AttendanceHoliday): Promise<void> {
  const { error } = await supabase.from('attendance_holidays').upsert(h, { onConflict: 'ngay' })
  if (error) throw error
}

export async function deleteHoliday(ngay: string): Promise<void> {
  const { error } = await supabase.from('attendance_holidays').delete().eq('ngay', ngay)
  if (error) throw error
}

/**
 * NV điểm danh — máy chủ kiểm IP mạng cơ quan trước, không khớp thì dùng GPS
 * (máy tính bắt buộc mạng cơ quan); ghi mã thiết bị để gắn cờ điểm danh hộ.
 */
export async function checkIn(
  kind: 'in' | 'out',
  pos: { lat: number; lng: number; accuracy: number } | null,
  device: { id: string; kind: 'mobile' | 'desktop' },
): Promise<AttendanceCheckin> {
  const { data, error } = await supabase.rpc('attendance_check_in', {
    p_kind: kind, p_lat: pos?.lat ?? null, p_lng: pos?.lng ?? null, p_accuracy: pos?.accuracy ?? null,
    p_device_id: device.id, p_device_kind: device.kind,
  })
  if (error) throw error
  return data as AttendanceCheckin
}

/** Điểm danh trong khoảng ngày — RLS tự giới hạn (NV: của mình; TP: phòng mình; admin: tất cả). */
export async function listCheckins(from: string, to: string, employeeIds?: string[]): Promise<AttendanceCheckin[]> {
  const base = () => supabase.from('attendance_checkins').select('*').gte('ngay', from).lte('ngay', to).order('checked_at')
  if (!employeeIds) {
    const { data, error } = await base()
    if (error) throw error
    return (data ?? []) as AttendanceCheckin[]
  }
  const out: AttendanceCheckin[] = []
  // Chia nhỏ danh sách NV để URL không quá dài
  for (let i = 0; i < employeeIds.length; i += 80) {
    const { data, error } = await base().in('employee_id', employeeIds.slice(i, i + 80))
    if (error) throw error
    out.push(...((data ?? []) as AttendanceCheckin[]))
  }
  return out
}

export async function listUnmatchedCheckins(): Promise<AttendanceCheckin[]> {
  const { data, error } = await supabase
    .from('attendance_checkins').select('*').is('employee_id', null)
    .order('checked_at', { ascending: false }).limit(500)
  if (error) throw error
  return (data ?? []) as AttendanceCheckin[]
}

/** Gán email/họ tên Google Form chưa khớp cho 1 NV — áp cho các lượt cũ + các lần đồng bộ sau. */
export async function mapCheckinIdentity(email: string | null, rawName: string | null, employeeId: string): Promise<number> {
  const { data, error } = await supabase.rpc('checkin_map_identity', {
    p_email: email, p_raw_name: rawName, p_employee_id: employeeId,
  })
  if (error) throw error
  return Number(data ?? 0)
}

export interface CheckinImportRow {
  checked_at: string; name: string; email: string; unit: string
  lat: string; lng: string; accuracy: string; distance: string; status: string
}

/** Admin nhập file điểm danh (Excel/CSV tải từ Google Sheets) — trùng lặp tự bỏ qua. */
export async function importCheckinRows(rows: CheckinImportRow[]): Promise<Record<string, number>> {
  const total: Record<string, number> = { inserted: 0, duplicate: 0, unmatched: 0, invalid: 0 }
  for (let i = 0; i < rows.length; i += 500) {
    const { data, error } = await supabase.rpc('checkin_import_rows', { p_rows: rows.slice(i, i + 500), p_source: 'import' })
    if (error) throw error
    for (const k of Object.keys(total)) total[k] += Number((data as Record<string, number> | null)?.[k] ?? 0)
  }
  return total
}

export async function listDailyAttendance(employeeIds: string[], from: string, to: string): Promise<AttendanceDailyRow[]> {
  const out: AttendanceDailyRow[] = []
  for (let i = 0; i < employeeIds.length; i += 80) {
    const { data, error } = await supabase
      .from('attendance_daily').select('id, employee_id, department_id, ngay, ky_hieu, ngoai_gio, so_km, ghi_chu')
      .in('employee_id', employeeIds.slice(i, i + 80)).gte('ngay', from).lte('ngay', to)
    if (error) throw error
    out.push(...((data ?? []) as AttendanceDailyRow[]))
  }
  return out
}

/** Lưu các ô công ngày đã thay đổi (upsert theo NV + ngày, chia chunk). */
export async function saveDailyAttendance(rows: AttendanceDailyRow[]): Promise<void> {
  const payload = rows.map(r => ({
    employee_id: r.employee_id, department_id: r.department_id, ngay: r.ngay,
    ky_hieu: r.ky_hieu, ngoai_gio: r.ngoai_gio, so_km: r.so_km,
  }))
  for (let i = 0; i < payload.length; i += 200) {
    const { error } = await withTimeout(
      supabase.from('attendance_daily').upsert(payload.slice(i, i + 200), { onConflict: 'employee_id,ngay' }),
      15_000, 'Lưu công ngày',
    )
    if (error) throw error
  }
}

export async function listApprovedLeavesInRange(employeeIds: string[], from: string, to: string): Promise<LeaveRequest[]> {
  const out: LeaveRequest[] = []
  for (let i = 0; i < employeeIds.length; i += 80) {
    const { data, error } = await supabase
      .from('leave_requests').select('*').eq('status', 'approved')
      .in('employee_id', employeeIds.slice(i, i + 80)).lte('from_date', to).gte('to_date', from)
    if (error) throw error
    out.push(...((data ?? []) as LeaveRequest[]))
  }
  return out
}

export async function getAttendanceSyncConfig(): Promise<AttendanceSyncConfig | null> {
  const { data, error } = await supabase.from('attendance_sync_config').select('*').eq('id', 1).maybeSingle()
  if (error) throw error
  return data as AttendanceSyncConfig | null
}

export async function updateAttendanceSyncConfig(
  patch: Partial<Pick<AttendanceSyncConfig, 'spreadsheet_id' | 'sheet_name' | 'enabled' | 'strict_checkin'>>,
): Promise<void> {
  const { error } = await supabase.from('attendance_sync_config').update(patch).eq('id', 1)
  if (error) throw error
}

/** Đồng bộ ngay từ Google Sheets (Edge Function checkin-sync-gsheet). */
export async function runCheckinSync(): Promise<Record<string, unknown>> {
  const { data, error } = await supabase.functions.invoke('checkin-sync-gsheet', { body: {} })
  if (error) {
    // HTTP ≠ 2xx: lấy thông điệp tiếng Việt trong body trả về
    const ctx = (error as { context?: Response }).context
    if (ctx && typeof ctx.json === 'function') {
      const body = await ctx.json().catch(() => null) as { error?: string } | null
      if (body?.error) throw new Error(body.error)
    }
    throw error
  }
  return data as Record<string, unknown>
}

// ─── Phân quyền hiển thị trang (v0.36, migration 032) ─────────────────────────

export async function listPageAccess(): Promise<import('@/lib/navigation').PageAccessRow[]> {
  const { data, error } = await supabase.from('page_access').select('path, roles, allow_users, deny_users')
  if (error) throw error
  return (data ?? []) as import('@/lib/navigation').PageAccessRow[]
}

export async function savePageAccess(rows: import('@/lib/navigation').PageAccessRow[]): Promise<void> {
  if (rows.length === 0) return
  const { error } = await supabase.from('page_access').upsert(rows, { onConflict: 'path' })
  if (error) throw error
}

// =============================================================
// Sổ giao ca bảo vệ (v0.40, migration 038)
// =============================================================

export type GuardShiftDraft = Omit<GuardShiftLog,
  'id' | 'created_at' | 'updated_at' | 'sign_giao_ten' | 'sign_giao_at' | 'sign_nhan_ten' | 'sign_nhan_at' | 'sign_duyet_ten' | 'sign_duyet_at'>

export async function listGuardShifts(limit = 30): Promise<GuardShiftLog[]> {
  const { data, error } = await supabase.from('guard_shift_logs').select('*')
    .order('ngay', { ascending: false }).order('ca', { ascending: false }).limit(limit)
  if (error) throw error
  return (data ?? []) as GuardShiftLog[]
}

export async function getGuardShift(ngay: string, ca: number, mucTieu: string): Promise<GuardShiftLog | null> {
  const { data, error } = await supabase.from('guard_shift_logs').select('*')
    .eq('ngay', ngay).eq('ca', ca).eq('muc_tieu', mucTieu).maybeSingle()
  if (error) throw error
  return data as GuardShiftLog | null
}

/** Tờ giao ca liền trước (cùng mục tiêu) — để ca nhận thấy việc tồn đọng. */
export async function getPrevGuardShift(ngay: string, ca: number, mucTieu: string): Promise<GuardShiftLog | null> {
  const { data, error } = await supabase.from('guard_shift_logs').select('*')
    .eq('muc_tieu', mucTieu)
    .or(`ngay.lt.${ngay},and(ngay.eq.${ngay},ca.lt.${ca})`)
    .order('ngay', { ascending: false }).order('ca', { ascending: false }).limit(1).maybeSingle()
  if (error) throw error
  return data as GuardShiftLog | null
}

/** Lưu tờ giao ca: chưa có id → thêm mới, có id → cập nhật. Chữ ký đi qua signGuardShift. */
export async function saveGuardShift(id: string | null, d: GuardShiftDraft): Promise<GuardShiftLog> {
  const q = id
    ? supabase.from('guard_shift_logs').update(d).eq('id', id).select().single()
    : supabase.from('guard_shift_logs').insert(d).select().single()
  const { data, error } = await q
  if (error) {
    if (error.code === '23505') throw new Error('Ca này đã có tờ giao ca — mở bản đã lưu ở danh sách bên dưới.')
    throw error
  }
  return data as GuardShiftLog
}

export async function signGuardShift(id: string, party: 'giao' | 'nhan' | 'duyet', name: string): Promise<GuardShiftLog> {
  const { data, error } = await supabase.rpc('guard_shift_sign', { p_id: id, p_party: party, p_name: name })
  if (error) throw error
  return data as GuardShiftLog
}

export async function isGuardCommander(): Promise<boolean> {
  const { data, error } = await supabase.rpc('is_guard_book_commander')
  if (error) return false
  return !!data
}

// =============================================================
// Lịch trực bảo vệ hằng tháng (v0.41, migration 040)
// =============================================================

/** NV tổ bảo vệ trong tháng (theo lịch sử công tác) — RPC vì RLS employees không cho xem chéo phòng. */
export async function listGuardMembers(monthYear: string): Promise<GuardMember[]> {
  const { data, error } = await supabase.rpc('guard_roster_members', { p_month: monthYear })
  if (error) throw error
  return (data ?? []) as GuardMember[]
}

/** Bảng phân công tháng — tổ bảo vệ chỉ thấy bản đã công bố (RLS). */
export async function getGuardRoster(monthYear: string, mucTieu: string): Promise<GuardRoster | null> {
  const { data, error } = await supabase.from('guard_rosters').select('*')
    .eq('month_year', monthYear).eq('muc_tieu', mucTieu).maybeSingle()
  if (error) throw error
  return data as GuardRoster | null
}

export async function saveGuardRoster(
  id: string | null,
  r: Pick<GuardRoster, 'month_year' | 'muc_tieu' | 'members' | 'cells' | 'note' | 'status'>,
): Promise<GuardRoster> {
  const q = id
    ? supabase.from('guard_rosters').update(r).eq('id', id).select().single()
    : supabase.from('guard_rosters').insert(r).select().single()
  const { data, error } = await q
  if (error) throw error
  return data as GuardRoster
}

/** Đơn nghỉ đã duyệt của tổ bảo vệ giao với tháng — để Lịch trực tự điền P (v0.42). */
export async function listGuardLeaves(monthYear: string): Promise<GuardLeave[]> {
  const { data, error } = await supabase.rpc('guard_roster_leaves', { p_month: monthYear })
  if (error) throw error
  return (data ?? []) as GuardLeave[]
}

/** Chuyển lịch trực sang Chấm công theo ngày: Ca 1/2/HC → X, Ca 3 → Đ, P → P/Ô/R; giữ ô TP nhập tay. */
export async function guardRosterToDaily(rosterId: string): Promise<{ written: number; kept: number }> {
  const { data, error } = await supabase.rpc('guard_roster_to_daily', { p_roster: rosterId })
  if (error) throw error
  return data as { written: number; kept: number }
}
